import { Button } from '@material-ui/core';
import { CommentsIcon, Description, UserIcon } from 'app/assets/images/icons';
import DotsMenu from 'app/components/DotsMenu';
import TextInput from 'app/components/Form/Inputs/TextInput';
import { useOnClickOutside } from 'app/hooks/use-outside-click';

import { dateFormatter } from 'app/utils/datePickerFormatter';
import { _t } from 'app/utils/i18n';
import { constants, getFromLocalStorage } from 'app/utils/localStorage';
import React, { FunctionComponent, useRef, useState } from 'react';

import styles from './AddComment.module.scss';

interface Props {
  removePosition?: boolean;
  compId: string;
  id?: number | string;
  style?: React.CSSProperties;
  values?: any;
  changeHandler?: any;
  chartId?: any;
}
const checkSlugAndId = (item: any, id: any, compId: any, chartId: any) => {
  if (chartId && !item.allow_multiple_values)
    return compId === item.slug && item.key === chartId;
  if (!item.allow_multiple_values) return compId === item.slug;

  return item.item_id === id && item.slug === compId;
};

const DescriptionBox = ({
  close,
  compId,
  values,
  changeHandler,
  id,
  chartId,
}: any) => {
  const [comment, setComment] = useState(
    values?.charts.filter((item: any) =>
      checkSlugAndId(item, id, compId, chartId)
    )?.[0]?.description
  );
  const [error, setError] = useState('');

  const ref = useRef<any>();
  useOnClickOutside(ref, close);

  const addCommentHandel = async () => {
    if (comment?.length === 0) {
      setError(_t('basic:err_required'));
      return;
    }
    if (comment?.length < 2) {
      setError(
        _t('basic:err_min', {
          fieldName: _t('reports:description'),
          length: 2,
        })
      );

      return;
    }

    changeHandler(
      'charts',
      values?.charts.map((item: any) =>
        checkSlugAndId(item, id, compId, chartId)
          ? {
              ...item,
              item_id: id,
              description: comment,
            }
          : item
      )
    );
    close();
    setError('');
  };

  return (
    <div className={styles.CommentBox} ref={ref}>
      <header>
        <h5>{_t('basic:description')}</h5>
      </header>

      <div className="pt-7 mb-10">
        <TextInput
          name={'comment'}
          autoHeight
          placeholder={_t('reports:description')}
          cols={80}
          value={comment}
          error={error}
          onChange={(value) => {
            setComment(value);
          }}
        />
      </div>

      <footer>
        <Button className="main-btn" onClick={addCommentHandel}>
          {_t('basic:submit')}
        </Button>
        <Button onClick={close}>{_t('basic:cancel')}</Button>
      </footer>
    </div>
  );
};

const CommentBox = ({
  close,
  compId,
  values,
  changeHandler,
  comments,
  id,
  chartId,
}: any) => {
  const [comment, setComment] = useState('');
  const [error, setError] = useState('');
  const userData = getFromLocalStorage(constants.user);
  const ref = useRef<any>();
  useOnClickOutside(ref, close);

  const deleteComment = (created_at: string, comment_id: string) => {
    changeHandler(
      'charts',
      values?.charts.map((item: any) =>
        checkSlugAndId(item, id, compId, chartId)
          ? {
              ...item,
              comments: item.comments.filter((i: any) =>
                !!i?.comment_id
                  ? i?.comment_id !== comment_id
                  : i?.created_at !== created_at
              ),
            }
          : item
      )
    );
  };
  const addCommentHandel = async () => {
    if (comment?.length === 0) {
      setError(_t('basic:err_required'));
      return;
    }
    if (comment?.length >= 2) {
      changeHandler(
        'charts',
        values?.charts.map((item: any) => {
          return checkSlugAndId(item, id, compId, chartId)
            ? {
                ...item,
                item_id: id,

                comments: [
                  ...(Array.isArray(item?.comments) ? item?.comments : []),
                  {
                    image_url: userData.image_url,
                    username: userData.name,
                    created_at: new Date().toISOString(),
                    comment,
                    id: 0,
                  },
                ],
              }
            : item;
        })
      );
      close();
      setComment('');
      setError('');
      return;
    }
    setError(
      _t('basic:err_min', {
        fieldName: _t('reports:comment'),
        length: 2,
      })
    );
  };

  return (
    <div className={styles.CommentBox} ref={ref}>
      <header>
        <h5>{_t('trends:comments')}</h5>
      </header>
      <div className={styles.CommentContainer}>
        {comments?.map((item: any, index: any) => (
          <section key={index}>
            <header>
              <div className={styles.ImageContainer}>
                <div>
                  {item?.image_url ? (
                    <img src={item?.image_url} alt="user image" />
                  ) : (
                    <UserIcon width={22} height={22} />
                  )}
                </div>
              </div>
              <div className={styles.HeaderInfo}>
                <div>
                  <h5>{item?.username}</h5>
                  <time>{dateFormatter(item?.created_at)}</time>
                </div>
                <DotsMenu
                  items={[{ name: _t('basic:delete'), id: item.comment_id }]}
                  onClick={() => {
                    deleteComment(item?.created_at, item?.comment_id);
                  }}
                />
              </div>
            </header>
            <p>{item?.comment}</p>
          </section>
        ))}
      </div>
      <div className="pt-7 mb-10">
        <TextInput
          name={'comment'}
          autoHeight
          placeholder={_t('reports:write_your_comment_here')}
          cols={80}
          value={comment}
          onChange={(value) => {
            setComment(value);
            error && setError('');
          }}
          error={error}
        />
      </div>

      <footer>
        <Button className="main-btn" onClick={addCommentHandel}>
          {_t('reports:comment')}
        </Button>
        <Button onClick={close}>{_t('basic:cancel')}</Button>
      </footer>
    </div>
  );
};

const AddComment: FunctionComponent<Props> = ({
  removePosition,
  compId,
  id = 0,
  values,
  changeHandler,
  chartId,
  style,
}) => {
  const [open, setOpen] = useState(false);
  const [decActive, setDecActive] = useState(false);

  const toggleComments = () => {
    setOpen((e) => !e);
    setDecActive(false);
  };
  const toggleDescription = () => {
    setDecActive((e) => !e);
    setOpen(false);
  };
  const comments = values?.charts?.filter((item: any) =>
    checkSlugAndId(item, id, compId, chartId)
  )?.[0]?.comments;

  return (
    <div style={style} className={styles.Container}>
      <div
        className={`${styles.AddComment} ${
          removePosition ? styles.AddComment_removePosition : ''
        } ${open ? styles.HigherZIndex : ''}`}
        data-testid="comment-holder"
      >
        <button className={styles.CommentsButton} onClick={toggleComments}>
          {!!comments?.length && <span>{comments && comments?.length}</span>}
          <CommentsIcon />
        </button>
        {open && (
          <CommentBox
            {...{
              compId,
              id,
              close: toggleComments,
              values,
              changeHandler,
              comments,
              open,
              chartId,
            }}
          />
        )}
      </div>
      <div
        className={`${styles.AddComment_Description} ${
          removePosition ? styles.AddComment_Description_removePosition : ''
        } ${decActive ? styles.HigherZIndex : ''}`}
      >
        <button className={styles.CommentsButton} onClick={toggleDescription}>
          <Description />
        </button>

        {decActive && (
          <DescriptionBox
            {...{
              compId,
              id,
              close: toggleDescription,
              values,
              changeHandler,
              comments,
              chartId,
            }}
          />
        )}
      </div>
    </div>
  );
};

export default AddComment;
